// Places each structured citation into the answer text as a
// <citation source-id="docUuid"/> tag, which remarkCitations renders as a
// chip, at the backend-derived character position. A citation without a
// usable position drops its chip — the source still shows in the drawer.

export type CitationInputType = {
  chunkId: string;
  snippet: string;
  /** Position of the supported claim, derived by the backend positioning
   * pass. */
  startIndex?: number | null | undefined;
  endIndex?: number | null | undefined;
};

type CodeRangeType = {
  start: number;
  end: number;
  kind: 'fence' | 'inline';
  closed: boolean;
};

/** Inline code spans and fenced code blocks — a tag injected inside one
 * renders as literal code text instead of a chip, so insertion points get
 * pushed past the closing delimiter. An unterminated fence (still
 * streaming) extends to the end of the text. */
const codeRanges = (text: string): CodeRangeType[] => {
  const ranges: CodeRangeType[] = [];

  for (const match of text.matchAll(/```[\s\S]*?(?:```|$)/g)) {
    const start = match.index;

    ranges.push({
      start,
      end: start + match[0].length,
      kind: 'fence',
      closed: match[0].length >= 6 && match[0].endsWith('```'),
    });
  }

  for (const match of text.matchAll(/`[^`\n]+`/g)) {
    const start = match.index;

    const insideFence = ranges.some(
      (r) => start >= r.start && start < r.end
    );

    if (!insideFence)
      ranges.push({
        start,
        end: start + match[0].length,
        kind: 'inline',
        closed: true,
      });
  }

  return ranges;
};

/** Null hides the chip until the range can take it (open fence mid-stream). */
const escapeCodeRange = ({
  end,
  ranges,
  text,
}: {
  end: number;
  ranges: CodeRangeType[];
  text: string;
}): number | null => {
  // An unclosed range's end is just the text edge, so the boundary itself
  // is still inside the code.
  const containing = ranges.find(
    (r) => end > r.start && (r.closed ? end < r.end : end <= r.end)
  );

  if (!containing) return end;

  if (!containing.closed) return null;

  if (containing.kind === 'inline') return containing.end;

  // Text on the closing-fence line reopens the block — the tag must start
  // the next line.
  return text[containing.end] === '\n' ? containing.end + 1 : containing.end;
};

type PlacementType = { docUuid: string; end: number };

type LineType = {
  start: number;
  end: number;
  indent: number;
  isBullet: boolean;
};

const analyseLines = (text: string): LineType[] => {
  const lines: LineType[] = [];

  let offset = 0;

  for (const raw of text.split('\n')) {
    const bullet = /^(\s*)(?:[-*+]|\d+\.)\s/.exec(raw);

    lines.push({
      start: offset,
      end: offset + raw.length,
      indent: bullet
        ? (bullet[1]?.length ?? 0)
        : raw.length - raw.trimStart().length,
      isBullet: !!bullet,
    });

    offset += raw.length + 1;
  }

  return lines;
};

/** The parent bullet of a nested bullet line, or null for top-level lines. */
const parentBulletIndex = ({
  lines,
  index,
}: {
  lines: LineType[];
  index: number;
}): number | null => {
  const line = lines[index];

  if (!line?.isBullet || line.indent === 0) return null;

  for (let i = index - 1; i >= 0; i--) {
    const candidate = lines[i];

    if (!candidate) break;

    if (candidate.isBullet && candidate.indent < line.indent) return i;

    // A flush-left paragraph ends the list.
    if (
      !candidate.isBullet &&
      candidate.indent === 0 &&
      candidate.end > candidate.start
    )
      return null;
  }

  return null;
};

/** A source repeated on sibling nested bullets reads as clutter — one chip
 * on the parent bullet says the same thing. Single nested chips stay put. */
const liftNestedPlacements = ({
  text,
  placements,
}: {
  text: string;
  placements: PlacementType[];
}): PlacementType[] => {
  const lines = analyseLines(text);

  const kept: PlacementType[] = [];

  const groups = new Map<
    string,
    { docUuid: string; parentEnd: number; members: PlacementType[] }
  >();

  for (const placement of placements) {
    const index = lines.findIndex(
      (l) => placement.end >= l.start && placement.end <= l.end
    );

    const parent =
      index === -1 ? null : parentBulletIndex({ lines, index });

    const parentLine = parent === null ? undefined : lines[parent];

    if (!parentLine) {
      kept.push(placement);

      continue;
    }

    const key = `${placement.docUuid}:${parent}`;

    const group = groups.get(key) ?? {
      docUuid: placement.docUuid,
      parentEnd:
        parentLine.start +
        text.slice(parentLine.start, parentLine.end).trimEnd().length,
      members: [],
    };

    group.members.push(placement);

    groups.set(key, group);
  }

  for (const group of groups.values()) {
    if (group.members.length >= 2) {
      kept.push({ docUuid: group.docUuid, end: group.parentEnd });
    } else {
      kept.push(...group.members);
    }
  }

  return kept;
};

export const injectCitationTags = ({
  text,
  citations,
}: {
  text: string;
  citations: readonly CitationInputType[];
}): string => {
  const ranges = codeRanges(text);

  const placements = citations.flatMap(({ chunkId, endIndex }) => {
    // docUuid#index (pineconeChunkId) — split inline; the @sc/ai-functions
    // barrel drags server deps into the browser bundle.
    const [docUuid = '', rest] = chunkId.split('#');

    if (!docUuid || rest === undefined) return [];

    if (
      typeof endIndex !== 'number' ||
      endIndex <= 0 ||
      endIndex > text.length
    )
      return [];

    const escaped = escapeCodeRange({ end: endIndex, ranges, text });

    return escaped === null ? [] : [{ docUuid, end: escaped }];
  });

  // Highest index first so earlier insertion points stay valid.
  return liftNestedPlacements({ text, placements })
    .sort((a, b) => b.end - a.end)
    .reduce(
      (body, { docUuid, end }) =>
        `${body.slice(0, end)}<citation source-id="${docUuid}"/>${body.slice(end)}`,
      text
    );
};
